import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Image,
  Radio,
  Row,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { CopyOutlined, DeleteOutlined, ExperimentOutlined, UploadOutlined } from "@ant-design/icons";
import PageTitle from "../../components/PageTitle";
import { testContractAIRecognition, testIDCardAIRecognition } from "../../api";

const { Text, Paragraph } = Typography;

const errorPayload = (error) => error?.response?.data || {
  success: false,
  error: error?.message || "请求失败",
};

const JsonResult = ({ value }) => {
  const json = useMemo(() => value ? JSON.stringify(value, null, 2) : "", [value]);
  if (!value) return <Text type="secondary">提交图片后，这里会显示服务端完整返回。</Text>;
  return (
    <div>
      {value.ai_raw_content && (
        <>
          <Text strong>模型原始文本</Text>
          <pre style={{
            margin: "8px 0 16px",
            padding: 12,
            maxHeight: 240,
            overflow: "auto",
            background: "#f5f7fa",
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
            {value.ai_raw_content}
          </pre>
        </>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <Text strong>原始返回 JSON</Text>
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={async () => {
            await navigator.clipboard.writeText(json);
            message.success("JSON 已复制");
          }}
        >
          复制
        </Button>
      </div>
      <pre style={{
        margin: 0,
        padding: 16,
        maxHeight: 520,
        overflow: "auto",
        color: "#d6e4ff",
        background: "#101828",
        borderRadius: 8,
        fontSize: 13,
        lineHeight: 1.55,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}>
        {json}
      </pre>
    </div>
  );
};

const PreviewList = ({ files, onRemove }) => (
  <Space wrap size={12} style={{ marginTop: 12 }}>
    {files.map((item) => (
      <Card
        key={item.uid}
        size="small"
        styles={{ body: { padding: 8 } }}
        cover={<Image src={item.preview} width={150} height={100} style={{ objectFit: "cover" }} />}
      >
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text ellipsis style={{ width: 105 }} title={item.name}>{item.name}</Text>
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onRemove(item.uid)} />
        </Space>
      </Card>
    ))}
  </Space>
);

const toPreviewFile = (file) => ({
  uid: file.uid,
  name: file.name,
  originFileObj: file,
  preview: URL.createObjectURL(file),
});

export default function AIRecognitionTest() {
  const [contractFiles, setContractFiles] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractResponse, setContractResponse] = useState(null);
  const [idCardFile, setIDCardFile] = useState(null);
  const [idCardSide, setIDCardSide] = useState("front");
  const [idCardLoading, setIDCardLoading] = useState(false);
  const [idCardResponse, setIDCardResponse] = useState(null);

  const removeContractFile = (uid) => {
    setContractFiles((current) => {
      const target = current.find((item) => item.uid === uid);
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((item) => item.uid !== uid);
    });
  };

  const addContractFiles = (file) => {
    setContractFiles((current) => {
      if (current.length >= 5) {
        message.warning("合同图片最多 5 张");
        return current;
      }
      return [...current, toPreviewFile(file)];
    });
    return false;
  };

  const chooseIDCard = (file) => {
    if (idCardFile) URL.revokeObjectURL(idCardFile.preview);
    setIDCardFile(toPreviewFile(file));
    return false;
  };

  const runContractTest = async () => {
    if (!contractFiles.length) {
      message.warning("请先选择合同图片");
      return;
    }
    const data = new FormData();
    contractFiles.forEach((item) => data.append("files", item.originFileObj));
    setContractLoading(true);
    setContractResponse(null);
    try {
      const response = await testContractAIRecognition(data);
      setContractResponse(response);
      message.success("合同 AI 测试完成");
    } catch (error) {
      const response = errorPayload(error);
      setContractResponse(response);
      message.error(response.error || "合同 AI 测试失败");
    } finally {
      setContractLoading(false);
    }
  };

  const runIDCardTest = async () => {
    if (!idCardFile) {
      message.warning("请先选择身份证图片");
      return;
    }
    const data = new FormData();
    data.append("file", idCardFile.originFileObj);
    data.append("side", idCardSide);
    setIDCardLoading(true);
    setIDCardResponse(null);
    try {
      const response = await testIDCardAIRecognition(data);
      setIDCardResponse(response);
      message.success("身份证 AI 测试完成");
    } catch (error) {
      const response = errorPayload(error);
      setIDCardResponse(response);
      message.error(response.error || "身份证 AI 测试失败");
    } finally {
      setIDCardLoading(false);
    }
  };

  const contractResult = contractResponse?.ai_result;
  const idCardResult = idCardResponse?.ai_result;

  return (
    <div>
      <PageTitle title="合同 / 身份证 AI 识别测试" />
      <Alert
        showIcon
        type="info"
        message="管理端独立测试工具"
        description="直接使用当前默认启用的合同 AI 配置。图片仅随本次请求送入 AI，不创建附件、不关联房间，也不修改业务数据。"
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title="合同 AI 识别" extra={<Tag color="blue">1～5 张</Tag>}>
            <Upload
              accept="image/*"
              multiple
              showUploadList={false}
              beforeUpload={addContractFiles}
              disabled={contractFiles.length >= 5 || contractLoading}
            >
              <Button icon={<UploadOutlined />}>选择合同图片</Button>
            </Upload>
            <PreviewList files={contractFiles} onRemove={removeContractFile} />
            <Divider />
            <Button
              type="primary"
              icon={<ExperimentOutlined />}
              loading={contractLoading}
              onClick={runContractTest}
              disabled={!contractFiles.length}
            >
              开始识别合同
            </Button>

            {contractResult && (
              <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}>
                <Descriptions.Item label="房源">{contractResult.property_name || "-"}</Descriptions.Item>
                <Descriptions.Item label="房间号">{contractResult.room_number || "-"}</Descriptions.Item>
                <Descriptions.Item label="租客手机号">{contractResult.tenant_phone || "-"}</Descriptions.Item>
                <Descriptions.Item label="月租金">{contractResult.monthly_rent ?? "-"}</Descriptions.Item>
                <Descriptions.Item label="押金">{contractResult.deposit_amount ?? "-"}</Descriptions.Item>
                <Descriptions.Item label="合同日期">
                  {contractResult.contract_start_date || "-"} ～ {contractResult.contract_end_date || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="其他固定月费">
                  {contractResult.other_fees?.length
                    ? contractResult.other_fees.map((fee) => `${fee.name}: ${fee.amount}`).join("；")
                    : "-"}
                </Descriptions.Item>
              </Descriptions>
            )}
            <Divider />
            <JsonResult value={contractResponse} />
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card title="身份证 AI 识别" extra={<Tag color="purple">单张</Tag>}>
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Radio.Group value={idCardSide} onChange={(event) => setIDCardSide(event.target.value)}>
                <Radio.Button value="front">人像面</Radio.Button>
                <Radio.Button value="back">国徽面</Radio.Button>
              </Radio.Group>
              <Upload accept="image/*" maxCount={1} showUploadList={false} beforeUpload={chooseIDCard}>
                <Button icon={<UploadOutlined />}>选择身份证图片</Button>
              </Upload>
              {idCardFile && (
                <PreviewList
                  files={[idCardFile]}
                  onRemove={() => {
                    URL.revokeObjectURL(idCardFile.preview);
                    setIDCardFile(null);
                  }}
                />
              )}
              <Button
                type="primary"
                icon={<ExperimentOutlined />}
                loading={idCardLoading}
                onClick={runIDCardTest}
                disabled={!idCardFile}
              >
                开始识别身份证
              </Button>
            </Space>

            {idCardResult && (
              <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}>
                <Descriptions.Item label="是否身份证">
                  <Tag color={idCardResult.is_id_card ? "success" : "error"}>
                    {idCardResult.is_id_card ? "是" : "否"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="识别版面">{idCardResult.card_side || "unknown"}</Descriptions.Item>
                <Descriptions.Item label="与所选版面一致">
                  <Tag color={idCardResponse.side_matched ? "success" : "warning"}>
                    {idCardResponse.side_matched ? "一致" : "不一致"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="姓名">{idCardResult.name || "-"}</Descriptions.Item>
                <Descriptions.Item label="身份证号">{idCardResult.id_number || "-"}</Descriptions.Item>
              </Descriptions>
            )}
            <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
              测试接口只展示 AI 判断，不会覆盖或保存上传图片。
            </Paragraph>
            <Divider />
            <JsonResult value={idCardResponse} />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
